'use client';

/** @app — application layer; owned per-fork (LMS / OTT). See PLATFORM_FILES.md. */
import { useTranslation } from 'react-i18next';
import type { LearningCourseDetail } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';

// AiHighlightsBlock — "intelligent summary" panel.
//
// Restores the AI section that the previous round inadvertently
// dropped. The old 1802-line page had an "Eğitim hakkında akıllı özet"
// section that rendered `course.aiHighlights`. This primitive brings
// that back as a reusable block — each variant places it at a
// different prominence level:
//
//   - Content-first: top of the curriculum tab — sets context before
//     the user scrolls a long chapter tree
//   - Decision-first: between the stats row and the outcomes panel
//     in the hero — primes the decision with AI-generated key points
//   - Trust-first: top of the outcomes tab (its default tab) —
//     sets context after the credibility-first hero
//
// Variants call this unconditionally; it returns `null` when
// `aiHighlights` is missing or empty so variants don't need to guard.

interface AiHighlightsBlockProps {
  highlights: LearningCourseDetail['aiHighlights'];
  /** Alternate title. Defaults to the `label.smartSummary` translation. */
  title?: string;
}

export function AiHighlightsBlock({ highlights, title }: AiHighlightsBlockProps) {
  const { t } = useTranslation('common');
  if (!highlights || highlights.length === 0) return null;
  const heading = title ?? t('label.smartSummary');
  return (
    <Card asChild className="ring-0">
      <div className="cd-ai-block" role="region" aria-label={heading}>
        <div className="cd-ai-head">
          <span className="cd-ai-badge" aria-hidden>
            <span className="cd-ai-sparkle">✦</span>
            AI
          </span>
          <h3 className="cd-ai-title">{heading}</h3>
        </div>
        <ul className="cd-ai-list">
          {highlights.slice(0, 5).map((h, i) => (
            <li key={i} className="cd-ai-item">
              <span className="cd-ai-marker" aria-hidden>
                ◆
              </span>
              <span>{h}</span>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}
